import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout } from '../components'
import { ImageWithFallback } from '../../../shared/components'
import { buildImageUrl } from '../../../config'
import statsService from '../../../services/statsService'
import { DashboardStats } from '../../../types/api'

const CSS_HANDLES = [
  'adminDashboard',
  'dashboardHeader',
  'dashboardTitle',
  'dashboardSubtitle',
  'statsGrid',
  'statCard',
  'statIcon',
  'statValue',
  'statLabel',
  'statChange',
  'statPositive',
  'statNegative',
  'chartsSection',
  'chartCard',
  'chartTitle',
  'chartContent',
  'recentOrders',
  'ordersTable',
  'orderRow',
  'orderId',
  'orderCustomer',
  'orderDate',
  'orderTotal',
  'orderStatus',
  'statusBadge',
  'statusPending',
  'statusConfirmed',
  'statusShipped',
  'statusDelivered',
  'topProducts',
  'productRow',
  'productImage',
  'productInfo',
  'productName',
  'productSales',
  'productRevenue',
  'quickActions',
  'actionCard',
  'actionIcon',
  'actionTitle',
  'actionDescription',
  'actionButton'
] as const

const AdminDashboard: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState<DashboardStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const hasLoaded = useRef(false)

  const renderSkeleton = () => (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header skeleton */}
        <div className="space-y-3">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-200" />
        </div>

        {/* Quick actions skeleton */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`quick-skeleton-${index}`} className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-200" />
              <div className="space-y-2">
                <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
              </div>
              <div className="h-10 w-full animate-pulse rounded-lg bg-gray-200" />
            </div>
          ))}
        </div>

        {/* Stats cards skeleton */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`stat-skeleton-${index}`} className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="space-y-3">
                  <div className="h-3 w-24 animate-pulse rounded bg-gray-200" />
                  <div className="h-6 w-20 animate-pulse rounded bg-gray-200" />
                  <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="h-12 w-12 animate-pulse rounded-lg bg-gray-200" />
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Recent orders skeleton */}
          <div className="lg:col-span-2 space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={`order-row-${index}`} className="h-12 animate-pulse rounded bg-gray-200" />
              ))}
            </div>
          </div>

          {/* Top products skeleton */}
          <div className="space-y-4 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="h-5 w-44 animate-pulse rounded bg-gray-200" />
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={`product-row-${index}`} className="flex items-center space-x-3">
                  <div className="h-12 w-12 animate-pulse rounded-lg bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
                    <div className="flex justify-between">
                      <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
                      <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )

  const fetchDashboard = useCallback(async () => {
    // Evitar execuções duplicadas (especialmente no React Strict Mode)
    if (hasLoaded.current) {
      return
    }

    hasLoaded.current = true
    setIsLoading(true)
    setError(null)
    try {
      const data = await statsService.getDashboard()
      setDashboardData(data)
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err)
      setError('Não foi possível carregar os dados do painel. Tente novamente.')
      // Resetar flag em caso de erro para permitir nova tentativa
      hasLoaded.current = false
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusBadge = (status: string) => {
    const normalized = status.toLowerCase()
    const statusConfig: Record<string, { label: string; className: string }> = {
      pending: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmado', className: 'bg-blue-100 text-blue-800' },
      processing: { label: 'Processando', className: 'bg-indigo-100 text-indigo-800' },
      shipped: { label: 'Enviado', className: 'bg-purple-100 text-purple-800' },
      delivered: { label: 'Entregue', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-700' },
      returned: { label: 'Devolvido', className: 'bg-gray-200 text-gray-800' }
    }

    const config = statusConfig[normalized] || statusConfig.pending
    return (
      <span className={`${handles.statusBadge} px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const quickActions = useMemo(
    () => [
      {
        title: 'Adicionar Produto',
        description: 'Cadastrar novo produto',
        icon: (
          <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        ),
        onClick: () => navigate('/admin/produtos')
      },
      {
        title: 'Gerenciar Pedidos',
        description: 'Ver todos os pedidos',
        icon: (
          <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        ),
        onClick: () => navigate('/admin/pedidos')
      },
      {
        title: 'Criar Cupom',
        description: 'Gerar novo cupom de desconto',
        icon: (
          <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
        ),
        onClick: () => navigate('/admin/cupons')
      },
      {
        title: 'Relatórios',
        description: 'Ver relatórios de vendas',
        icon: (
          <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        onClick: () => navigate('/admin/relatorios')
      }
    ],
    [navigate]
  )

  if (isLoading) {
    return renderSkeleton()
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="text-center space-y-4">
            <p className="text-gray-600">{error}</p>
            <button
              onClick={fetchDashboard}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  const totalRevenue = dashboardData?.totalRevenue ?? 0
  const totalOrders = dashboardData?.totalOrders ?? 0
  const totalProducts = dashboardData?.totalProducts ?? 0
  const totalCustomers = dashboardData?.totalCustomers ?? 0

  const recentOrders = dashboardData?.recentOrders?.slice(0, 10) ?? []
  const topProducts = dashboardData?.topProducts?.slice(0, 10) ?? []

  return (
    <AdminLayout>

      {/* Header */}
      <div className={`${handles.dashboardHeader} mb-8`}>
        <h1 className={`${handles.dashboardTitle} text-3xl font-bold text-gray-900 mb-2`}>
          Painel Administrativo
        </h1>
        <p className={`${handles.dashboardSubtitle} text-lg text-gray-600`}>
          Visão geral das operações da Baterpower Baterias
        </p>
      </div>

      <div className={`${handles.adminDashboard}`}>
        {/* Ações Rápidas */}
        <div className={`${handles.quickActions} mb-8`}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {quickActions.map((action, index) => (
              <div
                key={index}
                className={`${handles.actionCard} flex flex-col cursor-pointer rounded-lg bg-white p-6 shadow-sm transition-shadow hover:shadow-md`}
              >
                <div className={`${handles.actionIcon} mb-3`}>
                  {action.icon}
                </div>
                <h4 className={`${handles.actionTitle} mb-2 text-lg font-semibold text-gray-900`}>
                  {action.title}
                </h4>
                <p className={`${handles.actionDescription} mb-4 text-sm text-gray-600`}>
                  {action.description}
                </p>
                <button
                  onClick={action.onClick}
                  className={`${handles.actionButton} mt-auto w-full rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700`}
                >
                  Acessar
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Cards */}
        <div className={`${handles.statsGrid} grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8`}>
          {/* Vendas Totais */}
          <div className={`${handles.statCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${handles.statLabel} text-sm font-medium text-gray-600`}>
                  Vendas Totais
                </p>
                <p className={`${handles.statValue} text-2xl font-bold text-gray-900`}>
                  {formatPrice(totalRevenue)}
                </p>
              </div>
              <div className={`${handles.statIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-green-100`}>
                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total de Pedidos */}
          <div className={`${handles.statCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${handles.statLabel} text-sm font-medium text-gray-600`}>
                  Total de Pedidos
                </p>
                <p className={`${handles.statValue} text-2xl font-bold text-gray-900`}>
                  {totalOrders}
                </p>
              </div>
              <div className={`${handles.statIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100`}>
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total de Produtos */}
          <div className={`${handles.statCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${handles.statLabel} text-sm font-medium text-gray-600`}>
                  Total de Produtos
                </p>
                <p className={`${handles.statValue} text-2xl font-bold text-gray-900`}>
                  {totalProducts}
                </p>
              </div>
              <div className={`${handles.statIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-yellow-100`}>
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total de Clientes */}
          <div className={`${handles.statCard} rounded-lg bg-white p-6 shadow-sm`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`${handles.statLabel} text-sm font-medium text-gray-600`}>
                  Total de Clientes
                </p>
                <p className={`${handles.statValue} text-2xl font-bold text-gray-900`}>
                  {totalCustomers}
                </p>
              </div>
              <div className={`${handles.statIcon} flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100`}>
                <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Pedidos Recentes */}
          <div className={`${handles.recentOrders} lg:col-span-2`}>
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Pedidos Recentes
              </h3>
              <div className="overflow-x-auto">
                <table className={`${handles.ordersTable} w-full`}>
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Cliente</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Data</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Total</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentOrders.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                          Nenhum pedido recente encontrado.
                        </td>
                      </tr>
                    ) : (
                      recentOrders.map((order) => (
                        <tr key={order.id} className={`${handles.orderRow} border-b border-gray-100`}>
                          <td className={`${handles.orderId} px-4 py-3 text-sm font-medium text-gray-900`}>
                            {order.orderNumber || order.id}
                          </td>
                          <td className={`${handles.orderCustomer} px-4 py-3 text-sm text-gray-600`}>
                            <div className="font-medium text-gray-900">{order.customerName}</div>
                            <div className="text-xs text-gray-500">{order.customerEmail}</div>
                            <div className="text-xs text-gray-400">{order.firstItem}</div>
                          </td>
                          <td className={`${handles.orderDate} px-4 py-3 text-sm text-gray-600`}>
                            {formatDate(order.createdAt)}
                          </td>
                          <td className={`${handles.orderTotal} px-4 py-3 text-sm font-medium text-gray-900`}>
                            {formatPrice(order.total)}
                          </td>
                          <td className={`${handles.orderStatus} px-4 py-3`}>
                            {getStatusBadge(order.status)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Produtos Mais Vendidos */}
          <div className={`${handles.topProducts}`}>
            <div className="rounded-lg bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Produtos Mais Vendidos
              </h3>
              <div className="space-y-4">
                {topProducts.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                    Nenhum produto em destaque no momento.
                  </div>
                ) : (
                  topProducts.map((product) => (
                    <div key={product.id} className={`${handles.productRow} flex items-center space-x-3`}>
                      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                        <ImageWithFallback
                          src={buildImageUrl(product.imageUrl || undefined)}
                          alt={product.name}
                          className="h-full w-full object-cover"
                          fallbackWrapperClassName="flex h-full w-full items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100"
                          fallbackIconClassName="h-6 w-6 text-gray-300"
                        />
                      </div>
                      <div className={`${handles.productInfo} min-w-0 flex-1`}>
                        <p className={`${handles.productName} truncate text-sm font-medium text-gray-900`}>
                          {product.name}
                        </p>
                        <div className="flex justify-between text-xs text-gray-600">
                          <span className={`${handles.productSales}`}>
                            {product.sales} vendas
                          </span>
                          <span className={`${handles.productRevenue} font-medium`}>
                            {formatPrice(product.revenue)}
                          </span>
                        </div>
                        {product.brand && (
                          <div className="text-xs text-gray-400">Marca: {product.brand}</div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

export default AdminDashboard

